import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { useForm } from '@/common/form/FormContext';
import { Grid } from '@/common/grid/Grid';
import { HyperlinkCellRenderer } from '@/common/grid/cellRenderer/HyperlinkCellRenderer';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

const DISC_TYPES = [
  { value: 'GROSS_DISC', buttonLabel: 'Gross', posDisplayName: 'Gross POS', salesDisplayName: 'Gross Sales' },
  { value: 'ZLPA_DISC', buttonLabel: 'ZLPA', posDisplayName: 'ZLPA POS', salesDisplayName: 'ZLPA Sales' },
];

// Indexed by the row's tree level; level 0 is the discount pct, which has nothing to link to
const LEVEL_LINKS = [
  null,
  { module: 'md', entity: 'PROD', idField: 'prodId' },
  { module: 'md', entity: 'CUST', idField: 'custId' },
  { module: 'sc', entity: 'DIR_SALE', idField: 'dirSaleId' },
  { module: 'md', entity: 'CTRT', idField: 'ctrtId' },
];

const TreeLinkCellRenderer = (params) => {
  const link = LEVEL_LINKS[params.data?.level];
  if (!link || params.data[link.idField] == null) {
    return params.value;
  }
  return <HyperlinkCellRenderer {...params} colDef={{ ...params.colDef, ...link }} />;
};

const TopDiscGrid = ({ fieldDefs, rows }) => {
  const gridRef = useRef();
  const [isGridReady, setIsGridReady] = useState(false);

  useEffect(() => {
    if (isGridReady && gridRef.current && rows) {
      gridRef.current.setDataSource(rows);
    }
  }, [isGridReady, rows]);

  return (
    <Grid
      ref={gridRef}
      gridOptions={{
        fieldDefs,
        rowModelType: 'clientSide',
        treeData: true,
        groupDefaultExpanded: 0,
        statusBar: null,
      }}
      events={{
        onGridReady: () => setIsGridReady(true),
      }}
    />
  );
};

export const BestPriceTopDiscCard = () => {
  const form = useForm();
  const { prodExpr, custExpr, ctrtExpr } = form.formValues;

  const [discTypeValue, setDiscTypeValue] = useState(DISC_TYPES[0].value);

  const reqParam = useMemo(
    () => ({ discType: discTypeValue, prodExpr, custExpr, ctrtExpr }),
    [discTypeValue, prodExpr, custExpr, ctrtExpr],
  );

  const discType = DISC_TYPES.find((type) => type.value === discTypeValue);

  const fieldDefs = [
    {
      field: 'label',
      displayName: 'Discount / Product / Customer / Sale / Contract',
      autoGrouping: true,
      cellRenderer: TreeLinkCellRenderer,
      minWidth: 320,
    },
    { field: 'posPct', displayName: discType.posDisplayName + ' %', type: 'PERCENT', decimalPrecision: 2 },
    { field: 'ctrtPct', displayName: 'Max Contract %', type: 'PERCENT', decimalPrecision: 2 },
    { field: 'salesAmt', displayName: discType.salesDisplayName, type: 'MONEY' },
    { field: 'netSalesAmt', displayName: 'Net Sales Amt', type: 'MONEY' },
    { field: 'zpirSalesAmt', displayName: 'Z-Code Discount', type: 'MONEY' },
  ];

  const discTypeButtons = (
    <ToggleButtonGroup
      exclusive
      value={discTypeValue}
      onChange={(event, value) => {
        if (value) {
          setDiscTypeValue(value);
        }
      }}
      size="small"
    >
      {DISC_TYPES.map((type) => (
        <ToggleButton key={type.value} value={type.value}>
          {type.buttonLabel}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  return (
    <DashboardCard
      title={'Top Discounts'}
      buttons={discTypeButtons}
      // TODO: FIX DESCRIPTION
      description={'Displays the top discounts'}
      reqParam={reqParam}
      module="sc"
      plugin="BEST_PRICE_TOP_DISC_GRID_DATA_PLUGIN"
    >
      {(rows) => <TopDiscGrid key={discTypeValue} fieldDefs={fieldDefs} rows={rows} />}
    </DashboardCard>
  );
};
